/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2015, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'./FilterEntry',
	'./FilterSpecHelper'
],
function(_, FilterEntry, FilterSpecHelper) {
	'use strict';

	/**
	 * The QueryFilterSpec object is responsible for converting filter collections for presentation
	 * to the query engine (as a query spec).
	 * Collections may need to be pre-processed prior to adding
	 * (for example, dataPoint filters need to be merged with edge filters on the same dimension as 'or' terms rather than as separate filters).
	 * NOTE: Merged filters are ONLY FOR QUERY PURPOSES.  The filter collections are not changed
	 * (other than a temporary instance used for query generation).
	 */
	// Declare the class
	class FilterQuerySpec {

		/**
		 * Constructor
		 * @param visibleItemsMapParam - A map of the visible itemId's to an array of dataItemAPIs, layers/views they're in etc.
		 */
		constructor (visualization) {
			this._queryFilterSpec = [];
			this._dataSource = visualization && visualization.getDataSource();
			this._initializeDataItemMap(visualization);
		}

		_initializeDataItemMap(visualization) {
			this._dataItemsMap = {};

			if (visualization) {
				const slots = visualization.getSlots();
				const mappings = slots.getMappingInfoList();

				_.each(mappings, (mapping) => {
					const columnId = mapping.dataItem.getColumnId();
					const hasBinning = mapping.dataItem.getBinning();
					const filterId  = hasBinning ? columnId : FilterSpecHelper.getFilterId(columnId, mapping.dataItem.getAggregation());

					if (!this._dataItemsMap[filterId]) {
						this._dataItemsMap[filterId] = [];
					}

					const datasetIds = mapping.slot.getDefinition().getDatasetIdList();
					if(datasetIds.length > 1) {
						datasetIds.forEach(datasetId => {
							this._dataItemsMap[filterId].push({
								dataItem: mapping.dataItem,
								datasetId: datasetId
							});
						});
					} else {
						this._dataItemsMap[filterId].push({
							dataItem: mapping.dataItem,
							datasetId: datasetIds[0]
						});
					}
				});
			}
		}

		/**
		 * @returns true if need to combine isnull in exclude filter
		 * 'filters': [
			{
				'type': 'pre',
				'expression': {
					'or': [{
							'itemId': 'someItem',
							'operator': 'isnull'
						},
						{
							'operator': 'notin',
							'itemId': 'someItem',
							'values': [
								'XXX'
							]
						}
					]
				}
			}]
		 *
		 * The function should return false for an OLAP column
		 */
		_shouldApplyIsNullValueExpression(columnId) {
			if (!columnId || !this._dataSource) {
				return false;
			}
			const column = this._dataSource.getMetadataColumn(columnId);
			return column && !column.isOlapColumn();
		}

		/**
		 * incorporate a filter collection into the query filter spec
		 * AFTER merging include type datapoint with edge filters on the same items.
		 *
		 * @param filters - array of filter specs to add to the query spec
		 * @param options - options to the add (usu. exceptions which are defined for the main filter collection).
		 */
		addFiltersToSpec (filters, options) {
			options = options || {};
			this.addColumnFiltersToSpec(this._mergeEdgeAndPointFiltersOnSameColumns(filters, options), options);
		}

		/**
		 * incorporate the filters from a filter collection to the query filter spec.
		 * Note: this method should only be called if it is known that a filterCollection has only column-type filters (ie: no datapoint filters)
		 *		(or if the merge step has already been completed).
		 * @param filters - the collection to add to the spec
		 * @param options - options to the add (usu. exceptions which are defined for the main filter collection).
		 */
		addColumnFiltersToSpec (filters, options) {
			this._queryFilterSpec.push(...this._toSpec(filters, options));
		}

		/**
		 * @returns the query spec for addition to the filters element of the query.
		 */
		getFilterSpec () {
			return this._queryFilterSpec;
		}

		/**
		 * @returns true if there is a non-empty filter spec to add to the query.
		 */
		hasFilterSpec () {
			return (this._queryFilterSpec.length > 0);
		}

		/**
		 * @param filter - the filterEntry being processed
		 * @param exceptions - a map of the filter exceptions
		 * @param applyToCompoundFilters - true if the exceptions should be applied to compound filters.
		 * @param options - Object of options passed
		 * @returns true if this filterEntry should be excepted from the filters included in the query.
		 * (this usually corresponds to visible fields (or data points).
		 */
		_isFilterException (filter, exceptions, applyToCompoundFilters, options) {
			if (FilterSpecHelper.isEmpty(filter)) {
				return true;
			} else if (FilterSpecHelper.isOrdinal(filter)) {
				const projectedInAnyLayer = this._dataItemsMap[filter.id];
				if (!projectedInAnyLayer) {
					//This is column type range filter that's not projected in ANY layer.
					//...if not including non-projected range filters, this filter is a filter exception.
					if (options.excludeNonProjectedRangeFilters) {
						return true;
					}
				} else {
					//TODO: change layer to view.
					const projectedInThisLayer = _.find(projectedInAnyLayer, (projectedItem) => {
						return (projectedItem.datasetId === options.layerId);
					});
					if (!projectedInThisLayer) {
						//This is a dataitem type range filter but the item isn't projected in this layer.
						//Don't include it in the filters for this layer.
						return true;
					}
				}
			}

			// exceptions is built from _prepareFilterExceptionsMap in which exceptions map is built from filterExceptions or edgeFilterExceptions.
			// edgeFilterExceptions uses filter.id as the key while filterExceptions uses columnId as the key.
			// TODO Need to review columnId vs id then we follow the same rule for local filters.
			return 	exceptions[filter.columnId] || exceptions[filter.id] ||
					(applyToCompoundFilters && FilterSpecHelper.isCompound(filter) && filter.compoundFilterMatches(exceptions));
		}

		/**
		 * In the case where the same item is included  as both an edge filter and a dataPoint filter,
		 *	Eg: Year=2004 with (Year=2005, country=Canada) should yield all the countries with 2004 and an extra point for 2005,Canada
		 *		....we need a modified filter for query generation that combines the simple filter as a term of a compound one.
		 *
		 * In this case, we want to
		 * 1) keep the filters as 2 distinct entities for reporting purposes
		 * 2) combine the edge filters against those column(s) with the dataPoint filter as an additional 'or' term from the query perspective.
		 *
		 * To do this, we create a temporary filter collection and populate it with the combined result.
		 * NOTE: Merged filters are ONLY FOR QUERY PURPOSES.  The filter collections are not changed
		 * (other than a temporary instance used for query generation).
		 *
		 * @param filters - the filterCollection to be processed
		 * @returns EITHER...the original filterCollection (if the collection has no merging)
		 *			OR...a temporary collection with include type datapoint and edge filters on the same columns merged.
		 */
		_mergeEdgeAndPointFiltersOnSameColumns (filters, options) {
			const mergeInfo = this._prepareDataToMergeEdgeAndPointFiltersIfNeeded(filters, options);
			if (!mergeInfo) {
				return filters;	//No merging is necessary, return the original collection.
			}

			//There is an overlap of filters on at least one ColumnId for an edge and a point.
			const tempFilterCollection = [];
			_.each(filters, (filter) => {
				if (FilterSpecHelper.isCompound(filter)) {
					const finalCompoundFilter = this._createFinalCompoundFilter(filters, filter, mergeInfo);
					tempFilterCollection.push(finalCompoundFilter);
				}
			});

			//Make sure any filters that were NOT combined will be maintained as simple filters.
			//Note: ordinal filters (ranges) will never be combined so always need to be added to the target.
			_.each(filters, (filter) => {
				if (FilterSpecHelper.isOrdinal(filter) || (!FilterSpecHelper.isCompound(filter) && mergeInfo.unprocessedSimpleColumnIdMap[filter.columnId])) {
					const targetFilterEntry = JSON.parse(JSON.stringify(filter));
					tempFilterCollection.push(targetFilterEntry);
				}
			});
			return tempFilterCollection;
		}

		_createFilter(dataPointFilter) {
			const finalCompoundFilter = new FilterEntry(dataPointFilter);
			finalCompoundFilter._compoundFilterFromJSON();
			return finalCompoundFilter;
		}

		/**
		 * 'finalize' a datapointFilter (and, optionally, any overlapping edge filters) into a new compound filter.
		 * A finalized compound filter may simply be a copy of the original dataPoint filter
		 * OR, if edge selection filters on the same columns exist, the edge selection filters may be merged into the filter as a new 'or' term.
		 * Note: this only applies to include-type dataPoint filters.
		 * @param filters - the source filter collection
		 * @param dataPointFilter - the source dataPointFilter to be copied for addition to the new collection
		 * @param mergeInfo - information needed to decide whether combining is necessary.
		 * @returns a (possibly modified) copy of the original dataPoint filter to be added to the target collection.
		 */
		_createFinalCompoundFilter (filters, dataPointFilter, mergeInfo) {
			const finalCompoundFilter = this._createFilter(dataPointFilter);
			// Collect all of the columnID's for a particular compound filter
			// We're going to add the compound filter either 'as is' or with a new term (that is the overlap simple selection filter).
			if (dataPointFilter.operator === 'or') {
				// For each of the columnIds in the compound filter, lookup a simple selection filter on the original collection
				// if it exists.....merge the simple selection filter into the compound filter as an additional term of the 'or'.
				_.each(mergeInfo.compoundFilterColumnIds, (columnId) => {
					let filterId =  FilterSpecHelper.getFilterId(columnId);
					const simpleSelectionFiltersForThisCategory = filters.find(entry => entry.visualizationId && entry.id === filterId);
					if (simpleSelectionFiltersForThisCategory) {
						mergeInfo.unprocessedSimpleColumnIdMap[columnId] = undefined;
						const copyOfSimpleFilterTermToAdd = JSON.parse(JSON.stringify(simpleSelectionFiltersForThisCategory));
						finalCompoundFilter.values.add(copyOfSimpleFilterTermToAdd, { silent: true });
					}
				});
			}
			return finalCompoundFilter.toJSON();
		}

		/**
		 * Return information needed to merge edge and datapoint filters (if combining is necessary.....return null if not necessary).
		 * Merging means taking simple edge filter on a given ID and adding it as a new 'or term' on a datapoint filter (for query only)
		 * @param filters - the filterCollection to check.
		 * @param options - options which may include edgeFilterExceptions.
		 * @returns data needed for combining edge and datapoint filters IF combining is necessary (null if combining is not needed).
		 *			(mostly, the data needed is a map of all unprocessed simple filter columnIds).
		 */
		_prepareDataToMergeEdgeAndPointFiltersIfNeeded (filters, options) {
			// Case 1 where merge is unnecessary: there are no compound filters.
			const compoundFilterColumnIds = FilterSpecHelper.getSelectedCompoundFilterColumnIds(filters);
			if (compoundFilterColumnIds.length === 0) {
				return null;
			}
			// Case 2 where merge is unnecessary: there are no simple filters
			const simpleSelectionFilterColumnIds = FilterSpecHelper.getSelectedSimpleFilterColumnIds(filters, /** selectionFiltersOnly */ true);
			if (simpleSelectionFilterColumnIds.length === 0) {
				return null;
			}
			// Case 3 where merge is unnecessary: there are no overlapping columnIds in compound and simple filters.
			const overlappingColumnIds = _.intersection(simpleSelectionFilterColumnIds, compoundFilterColumnIds);
			if (overlappingColumnIds.length === 0) {
				return null;
			}
			// Case 4 where merge is unnecessary: the overlapping columnIds are all contained in the edgeFilterExceptions list.
			if (options.edgeFilterExceptions && _.difference(overlappingColumnIds, options.edgeFilterExceptions).length === 0) {
				return null;
			}

			//Merge IS necessary....
			const unprocessedSimpleColumnIdMap = {};
			const simpleColumnIds = FilterSpecHelper.getSelectedSimpleFilterColumnIds(filters);
			_.each(simpleColumnIds, (simpleColumnId) => {
				unprocessedSimpleColumnIdMap[simpleColumnId] = simpleColumnId;
			});

			return {
				simpleColumnIds: simpleColumnIds,
				compoundFilterColumnIds: compoundFilterColumnIds,
				unprocessedSimpleColumnIdMap: unprocessedSimpleColumnIdMap
			};
		}

		/**
		 * look up a dataItem that corresponds to this filterEntry (shares its itemId and aggregationType).
		 * For ranges, only a dataItem that is projected in this layer/view should be returned as an associated data item.
		 * If there's a match, set the filter against that item not against the column.
		 * @param filter - the filter entry (could be categorical or range)
		 * @param layerId - the layerId being processed.	//TODO: change to viewId.
		 */
		_getAssociatedDataItem (filter, layerId) {
			let associatedDataItem = null;
			if (!filter.filterBins && FilterSpecHelper.isRange(filter) && this._dataItemsMap[filter.id]) {
				associatedDataItem = _.find(this._dataItemsMap[filter.id], function(visibleItemInfo) {
					// Make sure the aggregation type matches for range filter.
					return visibleItemInfo.datasetId === layerId && visibleItemInfo.dataItem.getAggregation() === filter.aggregationType;
				});
			} else if (filter.filterBins) {
				associatedDataItem = _.find(this._dataItemsMap[filter.columnId], function(visibleItemInfo) {
					return visibleItemInfo.dataItem.getBinning() && visibleItemInfo.datasetId === layerId && filter.id.indexOf(visibleItemInfo.dataItem.getId()) !== -1;
				});
			}
			return (associatedDataItem && associatedDataItem.dataItem) || null;
		}

		/**
		 * Return the filter query spec for this filter entry.
		 * The query expression is of form:
		 * (for example)
		 * { type: "pre|post", expression: {itemId: 'itemId1', operator: 'in', values: [ v1, v2 ]} }
		 * or
		 * { type: "pre|post", expression: {itemId: 'itemId2', operator: 'between', values: [ min, max ] }
		 *
		 * For inclusion/exclusion of null categories:
		 * filterValues internally store an array of values that can include null to be included/excluded along with the other values.
		 * When generating a filterSpec, this value must be extracted and set as
		 * {
		 *	type: "pre|post",
		 *	expression: {
		 *		'or': [	{ itemId: 'ItemId1', operator: 'isnull' },
		 *				{ itemId: 'ItemId1', operator: 'in', values: [ v1, v2 ] }
		 *  	]
		 *  }
		 * }
		 * To use the above query to include null,
		 * set values = ["2017-02-08", "2017-02-09", "null"]
		 *
		 * For compound filters (like dataPoint filters), a similar technique is used.
		 * {
		 *	type: "pre|post",
		 *	expression: {
		 *		'or': [	{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ itemId: 'itemId2', operator: 'in', values: [v2]}]	},
		 *				{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ itemId: 'itemId2', operator: 'in', values: [v2]}]	}
		 *		]
		 *	}
		 * }
		 *  for compundFilter that contains filter on binned dataItem , the filter on binned dataItem will be removed
		 *  for example:
		 *  {
		 *  type: "pre|post",
		 *	expression: {
		 *		'or': [	{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ dataItem: 'itemId-binned', operator: 'in', values: [v2]}]	},
		 *				{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ itemId: 'itemId2', operator: 'in', values: [v2]}]	}
		 *		]
		 *	}
		 * }
		 * will be changed to
		 * {
		 *  type: "pre|post",
		 *	expression: {
		 *		'or': [	{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]}]	},
		 *				{	'and': [{ itemId: 'itemId1', operator: 'in', values: [v1]},
		 *							{ itemId: 'itemId2', operator: 'in', values: [v2]}]	}
		 *		]
		 *	}
		 * }
		 *
		 * @param options - options for query spec generation.
		 * @returns a filter query spec for this filter entry.
		 */
		_toQueryFilterSpec (filter, options) {
			options = options || {};
			if (FilterSpecHelper.isCompound(filter)) {
				const expressionSpec = {};
				// datapoint Exclude has 'not' as first operator
				if (typeof options.isDataPointExclude === 'undefined') {
					options.isDataPointExclude = filter.operator === 'not' ? true : false;
				}
				//At the root, we need to generate the filter type (pre|post) and the expression.
				const querySpec = (options.isSubExpression) ?
					expressionSpec : {
						type: filter.preOrPost || 'pre',
						expression: expressionSpec
					};
				expressionSpec[filter.operator] = [];

				//If the values in this tuple contains one or more numbers, we need to create a display type datapoint filter.
				const tupleContainsNumber = FilterSpecHelper.containsNumber(filter);
				_.each(filter.values, (value) => {
					if (!value.filterBins) {
						const subExpressionOptions = _.extend(options, {
							isSubExpression: true,
							tupleContainsNumber: tupleContainsNumber
						});
						const subExpressionSpec = this._toQueryFilterSpec(value, subExpressionOptions);
						if (!_.isEmpty(subExpressionSpec)) {
							expressionSpec[filter.operator] = expressionSpec[filter.operator].concat(subExpressionSpec);
							if (filter.operator === 'and' && options.isDataPointExclude && !options.nullIncludedInValueList && this._shouldApplyIsNullValueExpression(value.columnId)) { // Should not add isnull for OLAP sources
								expressionSpec[filter.operator] = expressionSpec[filter.operator].concat(this._buildNullValueExpressionQuerySpec(value, options));
							}
						}
					}
				});


				if (filter.operator === 'not') {
					if (!options.isSubExpression) {
						//Special case for negative dataPoint filters.  A 'not' operator is generated at the root.
						//The term of the not operator is not an array like it is for other operators.
						//The syntax is: (see dss spec for details.)
						//not: { or: [] } and not
						//not: [{ or: []}]
						querySpec.expression.not = querySpec.expression.not[0];
					} else {
						// NOT operator supports only one value
						expressionSpec[filter.operator] = expressionSpec[filter.operator][0];
					}

					if (querySpec.expression && typeof querySpec.expression.not === 'undefined') {
						querySpec.expression = {};
					}
				} else if (expressionSpec[filter.operator] && !expressionSpec[filter.operator].length) {
					delete expressionSpec[filter.operator];
				}
				return querySpec;
			} else {
				// Edge Exclude has 'notin' as first operator
				if (typeof options.isEdgeExclude === 'undefined') {
					options.isEdgeExclude = filter.operator === 'notin' ? true : false;
				}
			}
			return this._leafToQuerySpec(filter, options);
		}

		_leafToQuerySpec (filter, options) {
			if (FilterSpecHelper.isModel(filter)) {
				return {
					'type': 'pre',
					'expression': {
						'filterId': filter.columnId
					}
				};
			} else if ( filter.conditions ) {
				return this._buildConditionsQuerySpec(filter, options);
			} else {
				let expressionQuerySpec = {
					operator: filter.operator
				};
				//If this is a range filter, it might be a dataItem filter not a pure column filter.
				//This handles cases where the aggregation type of a range corresponds to the item.
				if (options.associatedDataItemAPI) {
					expressionQuerySpec.dataItem = options.associatedDataItemAPI.getId();
				} else if (filter.filterBins) {
					expressionQuerySpec.dataItem = filter.id;
				} else {
					expressionQuerySpec.itemId = filter.columnId;
				}
				if (options.tupleContainsNumber) {
					expressionQuerySpec.valueType = 'display';
				} else if (filter.type) {
					expressionQuerySpec.valueType = filter.type;
				}

				if (filter.values && filter.values.length > 0) {

					//Update the expressionQuerySpec with any nullValue inclusion/exclusion (if any)
					expressionQuerySpec = this._buildNullValuesQuerySpec(filter, expressionQuerySpec, filter.values, options);
				} else if (filter.valueDataItem) {
					// When there is no filter values
					expressionQuerySpec.valueDataItem = filter.valueDataItem;
				}
				let combiningQuerySpec = this._buildEdgeNullValueExpressionQuerySpec(filter, expressionQuerySpec, options);
				if (combiningQuerySpec) {
					expressionQuerySpec = combiningQuerySpec;
				}
				//By default preOrPost should be post for range filters and pre for category filters.
				const defaultPreOrPost = (FilterSpecHelper.isRange(filter)) ? 'post' : 'pre';
				//If there is any kind of user-specified preOrPost setting, use it.
				const preOrPost = filter.preOrPost || options.type || defaultPreOrPost;

				const filterQuerySpec = (options.isSubExpression) ?
					expressionQuerySpec : {
						type: preOrPost,
						expression: expressionQuerySpec
					};

				return filterQuerySpec;
			}
		}

		//create expressions { itemId: 'itemId1',operator: isnull } or { itemId: 'itemId1',operator: isnotnull }
		// based on it is edge exclude or data point exclude
		_buildNullValueExpressionQuerySpec (filter, options) {
			let operator;
			if (options.isDataPointExclude) {
				operator = 'isnotnull';
			} else if (options.isEdgeExclude) {
				operator = 'isnull';
			}
			if (operator) {
				const expressionQuerySpec = {
					itemId: filter.columnId,
					operator: operator
				};
				FilterSpecHelper.setBinningItemId(filter, expressionQuerySpec);
				return expressionQuerySpec;
			}
		}

		/**
		 * expression: { itemId: 'abcItem', operator: 'notin', values: ['a', 'b', 'c'] } will be:
		 * or: [{ itemId: 'abcItem',operator: isnull }, { itemId: 'abcItem', operator: 'notin', values: ['a', 'b', 'c'] } ]
		 * If filter is against relational data source.
		 * Returns null if is OLAP data source and the exclude expression will stay unchanged
		 */
		_buildEdgeNullValueExpressionQuerySpec (filter, expressionQuerySpec, options) {
			if (!filter.filterBins &&
				!expressionQuerySpec.valueDataItem && // exclude filter with empty values is converted to 'in valueDataItem...' so skip
				filter.operator === 'notin' &&
				options.isEdgeExclude &&
				!options.nullIncludedInValueList &&
				this._shouldApplyIsNullValueExpression(filter.columnId)) {
				const combiningQuerySpec = {};
				combiningQuerySpec['or'] = [this._buildNullValueExpressionQuerySpec(filter, options), expressionQuerySpec];
				return combiningQuerySpec;
			}
			return null;
		}

		_buildNullValuesQuerySpec (filter, expressionQuerySpec, aValueObject, options) {
			options = options || {};
			options.nullIncludedInValueList = false;
			//The spec expects 'value' for 'eq'/'noteq' and 'values' for 'in'/'notin'
			//var valueOrValues = (this.operator === 'eq') ? 'value' : 'values';
			const valueOrValues = 'values';
			//Set the polarity of how values will be filtered (include/exclude).
			const includeType = (filter.operator.indexOf('not') === 0) ? false : true;

			// Get rid of the explicit null entries in the array
			let valuesExcludingNull = _.without(aValueObject, null, 'null');

			// Get rid of the entries that have null u and d values
			valuesExcludingNull = _.filter(valuesExcludingNull, function(entry) {
				return (entry.u !== null && entry.d !== null) && (entry.u !== 'null' && entry.d !== 'null');
			});

			valuesExcludingNull = _.map(valuesExcludingNull, value => {
				if (typeof value === 'object') {
					return filter.type === 'display' || options.tupleContainsNumber ? value.d : value.u;
				}
				return value;
			});
			expressionQuerySpec[valueOrValues] = valuesExcludingNull;

			if (valuesExcludingNull.length < aValueObject.length) {
				options.nullIncludedInValueList = true;
				const filterNullsQuerySpec = {
					itemId: filter.columnId,
					operator: (includeType) ? 'isnull' : 'isnotnull'
				};

				FilterSpecHelper.setBinningItemId(filter, filterNullsQuerySpec);
				if (filter.type) {
					filterNullsQuerySpec.valueType = filter.type;
				}

				if (valuesExcludingNull.length > 0) {
					const combiningOperator = (includeType) ? 'or' : 'and';
					const combiningQuerySpec = {};
					combiningQuerySpec[combiningOperator] = [filterNullsQuerySpec, expressionQuerySpec];
					expressionQuerySpec = combiningQuerySpec;
				} else {
					expressionQuerySpec = filterNullsQuerySpec;
				}
			}
			return expressionQuerySpec;
		}

		_buildConditionsQuerySpec (filter, options){
			let valuesExp;
			if( filter.values && filter.values.length) {
				const aValueObject = filter.values;
				const aValues = _.pluck(aValueObject, filter.type === 'display' ? 'd' : 'u');
				valuesExp = {
					valueType: filter.type,
					'operator' : 'in',
					'itemId' : filter.columnId,
					'values' : aValues
				};
				FilterSpecHelper.setBinningItemId(filter, valuesExp);
			}
			let excludesExp;
			if( filter.excludedValues && filter.excludedValues.length) {
				const aExcludedValueObject = filter.excludedValues;
				const aExcludedValues = _.pluck(aExcludedValueObject, filter.type === 'display' ? 'd' : 'u');
				excludesExp = {
					valueType: filter.type,
					'operator' : 'notin',
					'itemId' : filter.columnId,
					'values' : aExcludedValues
				};
				FilterSpecHelper.setBinningItemId(filter, excludesExp);
			}

			const part1 = valuesExp ? { 'or': [ filter.conditions, valuesExp ] } : filter.conditions;
			let filterSpec = excludesExp ?  { 'and': [ part1, excludesExp ] } : part1;

			if ( filter.operator === 'notin') {
				filterSpec = { 'not': filterSpec };
			}
			if ( options.isSubExpression ) {
				return filterSpec;
			} else {
				return {
					type: options.type || 'pre',
					expression: filterSpec
				};
			}
		}

		/**
		 * FILTERAPI:
		 * Present the filters for this query as a simple array form needed by the query engine
		 * in the 'querySpec' url parameter.
		 * [{ column: columnindex, operator: {eq|in}, { value: value | values: [values...]},
		 *  { column: columnindex, operator: {eq|in}, { value: value | values: [values...]}
		 *  ...
		 * ]
		 * @param options - an object that customizes the behaviour of toSpec with the following members.
		 *		.edgeFilterExceptions: similar to filterExceptions except only edge filters are considered.
		 *			This is for localFilters against OLAP where an edge filter has been converted to a setSelection against a dataItem.
		 *			RULE: exclude any converted edge filters but process dataPoint filters normally (as filters).
		 *		.filterExceptions: a list of columnId's that, if matched, will not be added to the query
		 *			This is the options used by the old filters collection AND could potentially be used for the pageContext 'brushing case'
		 *			RULE: any visible edge items or matching points are excluded from filtering and hilighted
		 *			TODO: investigate either adapting pageContext to generate filters using queryFilterSpec or remove filterExceptions.
		 * @returns the array as shown above.
		 */
		_toSpec (filters, options) {
			const filterExceptionsMap = this._prepareFilterExceptionsMap(options);

			// Convert filters to the format used by the query spec
			const filterSpec = [];
			_.each(filters, (filter) => {
				// setup a filter id if not already exists
				filter.id = filter.id || FilterSpecHelper.getFilterId(filter.columnId, filter.aggregationType);

				if (!this._isFilterException(filter, filterExceptionsMap, /*applyToCompoundFilters*/!!(options && options.filterExceptions), options)) {
					const queryFilterSpec = this._toQueryFilterSpec(filter, {
						associatedDataItemAPI: this._getAssociatedDataItem(filter, options.layerId)
					});
					if (!_.isEmpty(queryFilterSpec.expression)) {
						if (options && options.dsf) {
							queryFilterSpec.dsf = options.dsf;  // add identifying attribute to filter so server can tell
						}
						filterSpec.push(queryFilterSpec);
					}
				}
			});
			return filterSpec;
		}

		/**
		 * if filterExceptions have been specified, convert the list to a map for fast lookup.
		 * Any column in the filterExceptionsMap will not be included in the query.
		 * @param filterExceptionsOption - a list of exceptions
		 * (filterExceptions are mostly specified for visible query fields on widget-to-widget filters so that data is hilighted not filtered).
		 */
		_prepareFilterExceptionsMap (options) {
			const exceptionsMap = {};
			const filterExceptionsOption = (options && (options.filterExceptions || options.edgeFilterExceptions)) || [];
			_.each(filterExceptionsOption, function(index) {
				exceptionsMap[index] = [];
			});
			return exceptionsMap;
		}
	}

	return FilterQuerySpec;
});
